import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Client-only global state. Anything that comes from the server belongs in
 * TanStack Query instead — do not mirror it here.
 */
type Theme = "light" | "dark" | "system";

interface UiState {
  theme: Theme;
  /** Desktop rail collapse state (persisted). Irrelevant below `md`. */
  sidebarOpen: boolean;
  /** Mobile off-canvas drawer state. Transient — always starts closed. */
  mobileSidebarOpen: boolean;
  setTheme: (theme: Theme) => void;
  toggleSidebar: () => void;
  openMobileSidebar: () => void;
  closeMobileSidebar: () => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: "system",
      sidebarOpen: true,
      mobileSidebarOpen: false,
      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      openMobileSidebar: () => set({ mobileSidebarOpen: true }),
      closeMobileSidebar: () => set({ mobileSidebarOpen: false }),
    }),
    {
      name: "mola-ui",
      // Only the desktop rail preference persists — the mobile drawer
      // always starts closed on a fresh page load.
      partialize: (state) => ({ theme: state.theme, sidebarOpen: state.sidebarOpen }),
      onRehydrateStorage: () => (state) => state && applyTheme(state.theme),
    },
  ),
);

function applyTheme(theme: Theme) {
  const dark =
    theme === "dark" ||
    (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", dark);
}
